import "./ApartmentManager.css";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_URL = import.meta.env.VITE_API_URL || "http://127.0.0.1:5000";

export default function ApartmentManager() {
    const navigate = useNavigate();

    const [apartments, setApartments] = useState([]);
    const [editing, setEditing] = useState(null);
    const [error, setError] = useState("");
    const [toast, setToast] = useState("");

    const [form, setForm] = useState({
        name: "",
        rent_amount: "",
        expenses: ""
    });

    const [editForm, setEditForm] = useState({
        name: "",
        rent_amount: "",
        expenses: ""
    });

    const showToast = (message) => {
        setToast(message);
        setTimeout(() => {
            setToast("");
        }, 2500);
    };

    const getAuthHeaders = () => {
        const token = localStorage.getItem("access_token");

        return {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        };
    };

    const handleUnauthorized = () => {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("username");
        localStorage.removeItem("role");
        navigate("/login");
    };

    useEffect(() => {
        fetchApartments();
    }, []);

    const fetchApartments = async () => {
        setError("");

        try {
            const r = await fetch(`${API_URL}/apartments/`, {
                headers: getAuthHeaders(),
            });

            if (r.status === 401 || r.status === 422) {
                handleUnauthorized();
                return;
            }

            const data = await r.json();

            if (!r.ok) {
                setError(data.error || "Gabim gjatë ngarkimit të apartamenteve.");
                return;
            }

            setApartments(data);
        } catch {
            setError("Nuk u arrit lidhja me serverin.");
        }
    };

    const saveApartment = async () => {
        setError("");

        if (!form.name.trim()) {
            setError("Emri i apartamentit është i detyrueshëm.");
            return;
        }

        try {
            const r = await fetch(`${API_URL}/apartments/`, {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    ...form,
                    rent_amount: Number(form.rent_amount || 0),
                    expenses: Number(form.expenses || 0),
                }),
            });

            const data = await r.json();

            if (r.status === 401 || r.status === 422) {
                handleUnauthorized();
                return;
            }

            if (!r.ok) {
                setError(data.error || "Gabim gjatë shtimit të apartamentit.");
                return;
            }

            setForm({ name: "", rent_amount: "", expenses: "" });
            showToast("Apartamenti u shtua me sukses.");
            fetchApartments();
        } catch {
            setError("Nuk u arrit lidhja me serverin.");
        }
    };

    const saveEdit = async () => {
        setError("");

        if (!editForm.name.trim()) {
            setError("Emri i apartamentit është i detyrueshëm.");
            return;
        }

        try {
            const r = await fetch(`${API_URL}/apartments/${editing}`, {
                method: "PUT",
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    ...editForm,
                    rent_amount: Number(editForm.rent_amount || 0),
                    expenses: Number(editForm.expenses || 0),
                }),
            });

            const data = await r.json();

            if (r.status === 401 || r.status === 422) {
                handleUnauthorized();
                return;
            }

            if (!r.ok) {
                setError(data.error || "Gabim gjatë përditësimit të apartamentit.");
                return;
            }

            setEditing(null);
            showToast("Apartamenti u përditësua me sukses.");
            fetchApartments();
        } catch {
            setError("Nuk u arrit lidhja me serverin.");
        }
    };

    const deleteApartment = async (id) => {
        setError("");

        if (!window.confirm("⚠ Je i sigurt që dëshiron të fshish këtë apartament?")) return;
        if (!window.confirm("‼ Konfirmo fshirjen përfundimtare")) return;

        try {
            const r = await fetch(`${API_URL}/apartments/${id}`, {
                method: "DELETE",
                headers: getAuthHeaders(),
            });

            const data = await r.json();

            if (r.status === 401 || r.status === 422) {
                handleUnauthorized();
                return;
            }

            if (!r.ok) {
                setError(data.error || "Gabim gjatë fshirjes së apartamentit.");
                return;
            }

            showToast("Apartamenti u fshi me sukses.");
            fetchApartments();
        } catch {
            setError("Nuk u arrit lidhja me serverin.");
        }
    };

    return (
        <div className="apartment-container">
            <h2 className="apartment-title">🏢 Menaxhimi i Apartamenteve</h2>

            {toast && (
                <div
                    style={{
                        position: "fixed",
                        top: "20px",
                        right: "20px",
                        background: "#16a34a",
                        color: "#fff",
                        padding: "12px 18px",
                        borderRadius: "12px",
                        boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
                        fontWeight: "600",
                        zIndex: 9999,
                        minWidth: "240px",
                    }}
                >
                    ✅ {toast}
                </div>
            )}

            {error && (
                <div style={{ marginBottom: "15px", color: "#b00020", fontWeight: "600" }}>
                    {error}
                </div>
            )}

            <div className="apartment-form">
                <input
                    type="text"
                    placeholder="Emri i apartamentit"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                />

                <input
                    type="number"
                    placeholder="Qiraja (Lek)"
                    value={form.rent_amount}
                    onChange={(e) => setForm({ ...form, rent_amount: e.target.value })}
                />

                <input
                    type="number"
                    placeholder="Shpenzime të pallatit (Lek)"
                    value={form.expenses}
                    onChange={(e) => setForm({ ...form, expenses: e.target.value })}
                />

                <button className="apartment-btn" onClick={saveApartment}>
                    ➕ Shto Apartament
                </button>
            </div>

            <table className="apartment-table">
                <thead>
                    <tr>
                        <th>Emri</th>
                        <th>Qiraja</th>
                        <th>Shpenzimet</th>
                        <th>Veprime</th>
                    </tr>
                </thead>

                <tbody>
                    {apartments.map((apt) => (
                        <tr key={apt.id}>
                            {editing === apt.id ? (
                                <>
                                    <td>
                                        <input
                                            value={editForm.name}
                                            onChange={(e) =>
                                                setEditForm({ ...editForm, name: e.target.value })
                                            }
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            value={editForm.rent_amount}
                                            onChange={(e) =>
                                                setEditForm({ ...editForm, rent_amount: e.target.value })
                                            }
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            value={editForm.expenses}
                                            onChange={(e) =>
                                                setEditForm({ ...editForm, expenses: e.target.value })
                                            }
                                        />
                                    </td>

                                    <td>
                                        <button className="edit-btn" onClick={saveEdit}>
                                            💾 Ruaj
                                        </button>
                                        <button
                                            className="cancel-btn"
                                            onClick={() => {
                                                setEditing(null);
                                                setEditForm({
                                                    name: "",
                                                    rent_amount: "",
                                                    expenses: ""
                                                });
                                            }}
                                        >
                                            ✖ Anulo
                                        </button>
                                    </td>
                                </>
                            ) : (
                                <>
                                    <td>{apt.name}</td>
                                    <td>{apt.rent_amount} Lek</td>
                                    <td>{apt.expenses} Lek</td>

                                    <td>
                                        <button
                                            className="edit-btn"
                                            onClick={() => {
                                                setEditing(apt.id);
                                                setEditForm({
                                                    name: apt.name || "",
                                                    rent_amount: apt.rent_amount || "",
                                                    expenses: apt.expenses || ""
                                                });
                                            }}
                                        >
                                            ✏ Ndrysho
                                        </button>

                                        <button
                                            className="delete-btn"
                                            onClick={() => deleteApartment(apt.id)}
                                        >
                                            🗑 Fshi
                                        </button>
                                    </td>
                                </>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
